import { FileDropZone, FileCard } from '@/components/ui';
import { Paperclip } from 'lucide-react';
import type { Attachment } from '@perfin/shared';

// ============================================================================
// TYPES
// ============================================================================

interface AttachmentsFieldProps {
  existingAttachments: Attachment[];
  pendingFiles: File[];
  onDrop: (files: File[]) => void;
  onRemoveExisting: (attachment: Attachment) => void;
  onRemovePending: (index: number) => void;
  disabled?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function AttachmentsField({
  existingAttachments,
  pendingFiles,
  onDrop,
  onRemoveExisting,
  onRemovePending,
  disabled = false,
}: AttachmentsFieldProps) {
  return (
    <div className="space-y-4">
      {/* File Drop Zone */}
      {!disabled && (
        <FileDropZone
          onDrop={onDrop}
          accept="image/*,.pdf,.doc,.docx,.txt,.xls,.xlsx,.csv"
          multiple
        />
      )}

      {/* Existing Attachments */}
      {existingAttachments.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500 uppercase flex items-center gap-1">
            <Paperclip className="h-3 w-3" />
            Attached ({existingAttachments.length})
          </p>
          {existingAttachments.map((attachment) => (
            <FileCard
              key={attachment.id}
              name={attachment.name}
              size={attachment.size}
              mimeType={attachment.mimeType}
              url={attachment.url}
              onRemove={disabled ? undefined : () => onRemoveExisting(attachment)}
            />
          ))}
        </div>
      )}

      {/* Pending Uploads */}
      {pendingFiles.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500 uppercase">
            Pending Upload ({pendingFiles.length})
          </p>
          {pendingFiles.map((file, index) => (
            <FileCard
              key={`pending-${file.name}-${index}`}
              name={file.name}
              size={file.size}
              mimeType={file.type}
              onRemove={disabled ? undefined : () => onRemovePending(index)}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {existingAttachments.length === 0 && pendingFiles.length === 0 && disabled && (
        <p className="text-sm text-gray-500 text-center py-4">
          No attachments
        </p>
      )}
    </div>
  );
}
